import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStreamAuth } from '../../hooks/useStreamAuth';
import { useProfile } from '../../contexts/ProfileContext';
import { useAppLoad } from '../../contexts/AppLoadContext';
import LoadingSpinner from './LoadingSpinner';
import spotlightService from '../../services/SpotlightService';

const ProfileRedirectHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isLoading: authLoading } = useStreamAuth();
  const { loading: appLoadLoading, needsProfileSelection: appNeedsProfileSelection } = useAppLoad();
  const {
    profiles,
    currentProfile,
    hasLoadedProfiles,
    isLoading: profileLoading,
    error: profileError,
    loadProfiles,
    needsProfileSelection,
    isSwitchingProfiles,
  } = useProfile();

  // Track if we've permanently failed (to prevent infinite loops)
  const [hasPermanentlyFailed, setHasPermanentlyFailed] = useState(false);

  // Load profiles immediately when user is authenticated (don't wait for appload)
  // CRITICAL: Wait for SpotlightService initialization before loading profiles
  // This ensures client token is ready before profile API calls (especially important for Safari)
  useEffect(() => {
    const initializeAndLoadProfiles = async () => {
      if (isAuthenticated && user && !hasLoadedProfiles && !profileLoading && !hasPermanentlyFailed) {
        console.log('🔧 ProfileRedirectHandler: Waiting for SpotlightService initialization...');
        
        // Wait for SpotlightService to finish initialization
        // This ensures the client token from Auth0 is properly set before we try to load profiles
        await spotlightService.initializationPromise;
        
        console.log('✅ ProfileRedirectHandler: SpotlightService initialized, loading profiles...');
        loadProfiles(false); // Not a manual retry
      }
    };
    
    initializeAndLoadProfiles();
  }, [isAuthenticated, user, hasLoadedProfiles, profileLoading, loadProfiles, hasPermanentlyFailed]);

  // Handle profile loading errors with retry logic (with circuit breaker)
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (profileError && !hasLoadedProfiles && !profileLoading && !hasPermanentlyFailed) {
      // Check if this is a network/fetch error (likely stale token)
      const isNetworkError = profileError.includes('Failed to fetch') || 
                            profileError.includes('NetworkError') ||
                            profileError.includes('client token missing');
      
      if (isNetworkError || retryCount >= maxRetries) {
        console.error('ProfileRedirectHandler: Permanent failure detected. Stopping retry attempts.');
        if (isNetworkError) {
          console.error('ProfileRedirectHandler: Network/Token error - likely stale authentication state');
        }
        setHasPermanentlyFailed(true);
        setShowError(true);
        return;
      }
      
      // Retry after a delay, but only if we haven't loaded profiles yet
      if (retryCount < maxRetries) {
        const retryTimer = setTimeout(() => {
          console.warn(`ProfileRedirectHandler: Retrying profile load (attempt ${retryCount + 1}/${maxRetries})`);
          setRetryCount(prev => prev + 1);
          loadProfiles(false); // Not a manual retry
        }, 5000 * (retryCount + 1)); // Exponential backoff: 5s, 10s, 15s

        return () => clearTimeout(retryTimer);
      }
    }
  }, [profileError, hasLoadedProfiles, profileLoading, loadProfiles, retryCount, hasPermanentlyFailed]);

  // Handle profile-based redirects
  useEffect(() => {
    // Don't redirect if still loading auth or profiles
    if (authLoading || profileLoading) {
      return;
    }

    // Don't redirect if not authenticated
    if (!isAuthenticated) {
      return;
    }

    // Don't redirect if we haven't loaded profiles yet
    if (!hasLoadedProfiles) {
      return;
    }

    // Don't redirect if already on profile-related pages
    if (location.pathname === '/profiles') {
      return;
    }

    // Don't redirect if user is explicitly switching profiles
    if (isSwitchingProfiles) {
      return;
    }

    let redirectTo = null;
    if (
      (location.pathname === '/' && location.search.length) ||
      location.pathname !== '/profiles'
    ) {
      redirectTo = '?after_selection=' + encodeURIComponent(location.pathname + location.search);
    }

    // Redirect to profile selection if needed
    if (needsProfileSelection()) {
      navigate('/profiles' + (redirectTo || ''), { replace: true });
      return;
    }

    // If user has profiles but no current profile selected, redirect to profiles
    // But only if they're not already on the profiles page (to avoid adding redirect params when switching)
    if (profiles.length > 0 && !currentProfile && location.pathname !== '/profiles') {
      navigate('/profiles' + (redirectTo || ''), { replace: true });
      return;
    }
  }, [
    authLoading,
    profileLoading,
    isAuthenticated,
    hasLoadedProfiles,
    location.pathname,
    needsProfileSelection,
    profiles.length,
    currentProfile,
    navigate,
    isSwitchingProfiles,
  ]);

  // Show error if permanent failure detected (stale token)
  if (showError && profileError) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#0a0a0a',
        color: 'white'
      }}>
        <div style={{
          maxWidth: '600px',
          padding: '40px',
          backgroundColor: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #333'
        }}>
          <h1 style={{ fontSize: '24px', marginBottom: '20px', color: '#ff4444' }}>
            ⚠️ Connection Error
          </h1>
          <p style={{ marginBottom: '20px', lineHeight: '1.6' }}>
            We're having trouble loading your profile. This usually happens when cached data becomes outdated.
          </p>
          <p style={{ marginBottom: '20px', fontSize: '14px', color: '#999' }}>
            Click the button below to clear your cache and try again.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '30px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Clear Cache & Retry
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '12px 24px',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking authentication
  if (authLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  // If AppLoadContext indicates we need profile selection, show loading
  // BUT allow the profiles page to render if we're already on it
  if (appNeedsProfileSelection && location.pathname !== '/profiles') {
    return <LoadingSpinner message="Loading profiles..." />;
  }

  // If authenticated but profiles are still loading, show loading
  if (isAuthenticated && (profileLoading || !hasLoadedProfiles)) {
    return <LoadingSpinner message="Loading profiles..." />;
  }

  // If authenticated and profiles are loaded, but need profile selection, show loading
  // BUT allow the profiles page to render if we're already on it
  if (
    isAuthenticated &&
    hasLoadedProfiles &&
    needsProfileSelection() &&
    location.pathname !== '/profiles'
  ) {
    return <LoadingSpinner message="Selecting profile..." />;
  }

  // If authenticated and profiles are loaded, but no current profile, show loading
  // BUT allow the profiles page to render if we're already on it
  if (
    isAuthenticated &&
    hasLoadedProfiles &&
    profiles.length > 0 &&
    !currentProfile &&
    location.pathname !== '/profiles'
  ) {
    return <LoadingSpinner message="Selecting profile..." />;
  }

  // Render children once everything is loaded and checked
  return children;
};

export default ProfileRedirectHandler;
